import { useState, useEffect } from "react";
import { Loader2, Eye, CheckCircle2, Star, TrendingUp, Trophy, BookOpen, ListPlus } from "lucide-react";
import { StatWidget } from "./StatWidget";
import { ProgressBar, tierOf } from "./ProgressBar";
import { Progress } from "./ui/progress";
import { useSetAsset } from "../hooks/useSetAsset";
import { handleSetAssetError } from "../lib/images";
import { fetchCollectionStats, fetchCollectionCompletion, fetchPokedex, fetchLists } from "../lib/api";

// Même composant que celui d'App.js (dupliqué, pas exporté là-bas) : un
// Hook ne peut pas s'appeler directement dans un .map(), il lui faut son
// propre composant.
const SetSymbolIcon = ({ setId, symbol }) => {
  const src = useSetAsset(setId, "symbol", symbol);
  if (!src) return null;
  return <img src={src} alt="" className="w-6 h-6 object-contain" onError={handleSetAssetError} />;
};

// Vue en LECTURE SEULE de la collection d'un ami (cf. section "Amis" dans la
// nav). Stats + progression par collection ; cliquer une collection ouvre
// son détail carte par carte (App.js, en lecture seule — cf. useFriendCollection).
export const FriendCollectionView = ({ friendId, username, onOpenSet, onOpenList }) => {
  const [stats, setStats] = useState(null);
  const [completion, setCompletion] = useState([]);
  const [pokedex, setPokedex] = useState(null);
  const [sharedLists, setSharedLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchCollectionStats(friendId),
      fetchCollectionCompletion(friendId),
      fetchPokedex(friendId),
      fetchLists(friendId),
    ]).then(([s, c, p, l]) => {
      if (cancelled) return;
      setStats(s); setCompletion(c); setPokedex(p); setSharedLists(l);
    }).catch(err => console.error("Collection d'ami :", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [friendId]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-red-500" /></div>;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Eye className="w-5 h-5 text-sky-400" />
        <h2 className="text-2xl font-bold text-white">Collection de {username}</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Lecture seule — tu ne peux rien modifier ici.
      </p>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <StatWidget icon={CheckCircle2} label="Cartes possédées" value={stats.owned_unique}
            subtext={`${stats.owned_total} au total`} color="bg-emerald-500" compact />
          <StatWidget icon={Star} label="Recherchées" value={stats.wanted_count}
            subtext="Sur sa liste" color="bg-blue-500" compact />
          <StatWidget icon={TrendingUp} label="Valeur estimée"
            value={stats.estimated_value > 0 ? `${stats.estimated_value.toFixed(2)} €` : "—"}
            subtext="Prix tendance Cardmarket" color="bg-red-500" compact />
        </div>
      )}

      {pokedex && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-400" />Pokédex national
          </h3>
          <div className="flex items-center gap-3">
            <Progress value={pokedex.national.total ? (pokedex.national.owned / pokedex.national.total) * 100 : 0}
              className="h-2 bg-zinc-800 flex-1" />
            <span className="text-sm text-zinc-400 shrink-0">
              {pokedex.national.owned} / {pokedex.national.total}
            </span>
          </div>
        </div>
      )}

      {sharedLists.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-red-500" />Listes partagées
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sharedLists.map(list => (
              <button key={list.id} className="set-completion-card text-left"
                onClick={() => onOpenList?.(list.id)}>
                <h4 className="text-sm font-semibold text-white truncate">{list.name}</h4>
                <p className="text-xs text-zinc-500">{list.item_count} carte{list.item_count > 1 ? "s" : ""}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {completion.length > 0 ? (
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />Progression des collections
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completion.map(set => (
              <button key={set.set_id} className={`set-completion-card set-completion-tier-${tierOf(set.completion_percentage)} text-left`}
                onClick={() => onOpenSet?.(set.set_id)}>
                <div className="flex items-center gap-3 mb-3">
                  {set.set_symbol && <div className="set-completion-symbol"><SetSymbolIcon setId={set.set_id} symbol={set.set_symbol} /></div>}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-white truncate">{set.set_name}</h4>
                    <p className="text-xs text-zinc-500 truncate">{set.set_series}</p>
                  </div>
                  <span className="text-lg font-bold text-emerald-400">{set.completion_percentage}%</span>
                </div>
                <ProgressBar size="md" pct={set.completion_percentage} owned={set.owned_cards} total={set.total_cards} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-zinc-500">Aucune collection entamée pour l'instant.</p>
      )}
    </div>
  );
};
